import { AxiosRequestConfig, AxiosResponse } from "axios";

import { AccessRight } from "@/shared/domain/user";
import { pi2AuthTokenProvider } from "@/shared/hooks/useAuth/providers";

import { getSpApi, requestConfig } from "./apiRoot";
import { fAuth, fAuthItsme, fSaml, spname } from "./authApi";
import { UrlManager } from "./UrlManager";

// Swagger: https://ibpcsrcdev01.portima.net/mybrokerapi/swagger/index.html

export const getUserStatus = async (email: string, officeId?: string): Promise<MyBrokerApi.UserStatus> => {
    const axiosRequestConfig = requestConfig<Paths.ApiUserMailStatus.Get.QueryParameters>({
        params: {
            email: email.toLowerCase(),
            officeId,
        },
    });
    const { data } = await getSpApi().get<Paths.ApiUserStatus.Get.Responses.$200>(
        UrlManager.userStatus(),
        axiosRequestConfig,
    );
    return data;
};

export const authenticate = async (loginToken: string, officeId: string): Promise<MyBrokerApi.JwtToken> => {
    const { data } = await getSpApi().post<MyBrokerApi.JwtToken>(UrlManager.authenticate(), {
        sp: spname,
        type: "JWT",
        token: loginToken,
        officeId,
    });
    return data;
};

// prettier-ignore
export const resetPasswordInit = async (init: MyBrokerApi.ResetPasswordInitModel): Promise<MyBrokerApi.ApiSuccessAccepted> => {
    const body = { ...init };
    const { data } = await getSpApi().post<MyBrokerApi.ApiSuccessAccepted>(UrlManager.resetPasswordInit(), body);
    return data;
};

export const resetPasswordVerifyGuid = async (guid: string): Promise<MyBrokerApi.ApiSuccessAccepted> => {
    const { data } = await getSpApi().post<MyBrokerApi.ApiSuccessAccepted>(UrlManager.resetPasswordVerifyGuid(guid));
    return data;
};

export const resetPasswordComplete = async (
    commit: MyBrokerApi.ResetPasswordCommitModel,
): Promise<MyBrokerApi.ApiSuccessAccepted> => {
    const body = { ...commit };
    const { data } = await getSpApi().post<MyBrokerApi.ApiSuccessAccepted>(UrlManager.resetPasswordComplete(), body);
    return data;
};

export const signUp = async (registration: MyBrokerApi.RegistrationModel): Promise<void> => {
    return getSpApi().post<MyBrokerApi.RegistrationModel, void>(UrlManager.registerSignup(), registration);
};

export const resendEmail = async (resendEmailValues: MyBrokerApi.ResendEmailModel): Promise<void> => {
    return getSpApi().post<MyBrokerApi.ResendEmailModel, void>(UrlManager.registerResendEmail(), resendEmailValues);
};

export const getRegisterTokenValidate = async (token: string): Promise<number> => {
    const axiosRequestConfig = requestConfig<Paths.ApiUserRegisterTokenValidation.Get.QueryParameters>({
        params: {
            id: token,
        },
    });

    const { status } = await getSpApi().get<void>(UrlManager.registerTokenValidation(), axiosRequestConfig);
    return status;
};

export const getActivateTokenValidate = async (token: string): Promise<MyBrokerApi.UserAuth> => {
    const axiosRequestConfig = requestConfig<Paths.ApiUserActivateTokenValidation.Get.QueryParameters>({
        params: {
            token,
        },
    });

    const { data } = await getSpApi().get<MyBrokerApi.UserAuth>(
        UrlManager.activateTokenValidation(),
        axiosRequestConfig,
    );
    return data;
};

export const registerComplete = async (request: {
    body: {
        token: string;
        password: string;
    };
}): Promise<MyBrokerApi.RegistrationCompletedResponse> => {
    const { body } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserRegisterComplete.Post.QueryParameters>();

    const { data } = await getSpApi().post<MyBrokerApi.RegistrationCompletedResponse>(
        UrlManager.registerComplete(),
        body,
        axiosRequestConfig,
    );
    return data;
};

export const activateComplete = async (request: {
    body: MyBrokerApi.UserAuth;
    params: {
        token: string;
    };
}): Promise<void> => {
    const { body, params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserActivateComplete.Post.QueryParameters>({ params });

    return getSpApi().post<MyBrokerApi.UserAuth, void>(UrlManager.activateComplete(), body, axiosRequestConfig);
};

export const auth = async (email: string, password: string): Promise<string> => {
    const res = await fAuth(email, password);
    const pi2AuthToken = res?.headers?.pi2authtok;
    if (pi2AuthToken) {
        pi2AuthTokenProvider.setToken(pi2AuthToken);
    }
    return pi2AuthToken;
};

export const authItsme = async (itsmeUserId: string, itsmeToken: string): Promise<string> => {
    const res = await fAuthItsme(itsmeUserId, itsmeToken);
    const pi2AuthToken = res?.headers?.pi2authtok;
    if (pi2AuthToken) {
        pi2AuthTokenProvider.setToken(pi2AuthToken);
    }
    return pi2AuthToken;
};

/**
 * loginSilent (fSaml & authenticate)
 * Must call auth before calling login
 */
export const loginSilent = async (officeId: string, fromItsMe = false): Promise<MyBrokerApi.JwtToken> => {
    const samlResult = await fSaml(officeId, fromItsMe);
    const authResult = await authenticate(samlResult.artifact, officeId);
    return authResult;
};

export const loginScoped = async (context: string): Promise<MyBrokerApi.JwtToken> => {
    const { data } = await getSpApi().post<{ context: string }, AxiosResponse<MyBrokerApi.JwtToken>>(
        UrlManager.authenticateScoped(),
        {
            context,
        },
    );
    return data;
};

export const itsmeInfo = async (itsMeUid: string): Promise<MyBrokerApi.ItsMeUserInfo> => {
    const axiosRequestConfig = requestConfig<Paths.ApiUserItsmeInfo.Get.QueryParameters>({
        params: {
            itsMeUid,
        },
    });
    const { data } = await getSpApi().get<MyBrokerApi.ItsMeUserInfo>(UrlManager.itsmeInfo(), axiosRequestConfig);
    return data;
};

export const itsmeSignupInit = async (request: {
    body: MyBrokerApi.RegistrationModel;
    params: {
        itsMeUid: string;
    };
}): Promise<MyBrokerApi.ApiSuccessOk> => {
    const { body, params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserItsmeSignupInit.Post.QueryParameters>({ params });

    const { data } = await getSpApi().post(UrlManager.itsmeSignupInit(), body, axiosRequestConfig);
    return data;
};

export const itsmeSignupComplete = async (request: {
    params: {
        token: string;
    };
}): Promise<MyBrokerApi.ItsMeUserInfo> => {
    const { params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserItsmeSignupComplete.Post.QueryParameters>({ params });

    const { data } = await getSpApi().post(UrlManager.itsmeSignupComplete(), null, axiosRequestConfig);
    return data;
};

export const itsmeRegister = async (request: {
    body: MyBrokerApi.RegistrationModel;
    params: {
        itsMeUid: string;
    };
}): Promise<MyBrokerApi.ItsMeUserInfo> => {
    const { body, params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserItsmeRegister.Post.QueryParameters>({ params });

    const { data } = await getSpApi().post(UrlManager.itsmeRegister(), body, axiosRequestConfig);
    return data;
};

export const itsmeActivateTokenValidation = async (request: {
    params: {
        token: string;
    };
}): Promise<MyBrokerApi.UserAuth> => {
    const { params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserItsmeActivateTokenValidation.Get.QueryParameters>({ params });

    const { data } = await getSpApi().get(UrlManager.activateItsmeTokenValidation(), axiosRequestConfig);
    return data;
};

export const itsmeActivateComplete = async (request: {
    params: {
        itsMeUid: string;
        email: string;
        officeId: string;
        language: string;
        token: string;
    };
}): Promise<MyBrokerApi.ItsMeUserInfo> => {
    const { params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiUserItsmeActivateComplete.Post.QueryParameters>({ params });

    const { data } = await getSpApi().post(UrlManager.itsmeActivateComplete(), null, axiosRequestConfig);
    return data;
};

export const getAccessRights = async (accessToken: string): Promise<AccessRight[]> => {
    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    };
    const { data } = await getSpApi().get<MyBrokerApi.AccessRightPayload>(UrlManager.accessRight(), config);
    return data.result;
};
